import axios from "axios";
import get from "lodash/get";
import config from "@/config/env.config.json";
import logger from "@/utils/logger";
import constants from "@/utils/constants";
import { shouldUseConsumerApiLatestReading } from "@/utils/readingsHelpers";

const envConfig = config[process.env.NODE_ENV];
const dataApiBaseUrl = envConfig.dataApiBaseUrl;
const consumerBaseUrl = envConfig.consumerApiBaseUrl;
const readingsApi = {};

// Add a simple cache object
const cache = {};

async function getReadingsFromCache(userProfileId, deviceId, readingType) {
  // Retrieve readings from cache, based on userProfileId, deviceId, and readingType
  const cacheKey = `${userProfileId}_${deviceId}_${readingType}`;
  const cacheEntry = cache[cacheKey];
  if (cacheEntry && !isCacheExpired(cacheEntry.timestamp)) {
    return cacheEntry.data;
  }
  return null;
}

function setReadingsInCache(userProfileId, deviceId, readingType, data) {
  // Store readings in cache, based on userProfileId, deviceId, readingType, and data
  const cacheKey = `${userProfileId}_${deviceId}_${readingType}`;
  cache[cacheKey] = {
    data,
    timestamp: new Date().getTime(),
  };
}

function isCacheExpired(timestamp) {
  // Check if the cache entry has expired based on its timestamp and a fixed cache duration.
  const currentTime = new Date().getTime();
  const cacheDuration = 3 * 60 * 1000; // Cache duration in milliseconds (3 minute)
  return currentTime - timestamp > cacheDuration;
}

async function fetchDataFromDataApi(deviceParam, userProfileId, readingType) {
  // Fetch readings data from the Data API based on deviceParam, userProfileId, and readingType.
  try {
    const response = await axios.get(
      `${dataApiBaseUrl}/readings/${readingType}/${deviceParam}/${userProfileId}`
    );
    return response.data;
  } catch (error) {
    logger.error("Error in fetchDataFromDataApi:", error);
    throw error;
  }
}

async function fetchLatestReadingFromConsumerApi(userProfileId, deviceId) {
  // Fetch the latest reading data for a given userProfileId and deviceId from the Consumer API using the readingsApi library,
  // and handle any errors by logging and returning null.
  try {
    const latestReading = await readingsApi.getLatestReadingFromConsumerApi(
      userProfileId,
      deviceId
    );
    return latestReading;
  } catch (error) {
    logger.error("Error in fetchLatestReadingFromConsumerApi:", error);
    return null;
  }
}

readingsApi.getReadingsForDevice = async (
  deviceCategory,
  userProfileId,
  readingType,
  deviceId
) => {
  // Get readings data for a device from the Data API and Consumer API, handling errors and caching the result.
  logger.info(deviceId);
  if (!readingType || (readingType !== "latest" && readingType !== "all")) {
    throw new Error("Reading type must be latest or all");
  }

  const deviceParam =
    constants.DEVICE_TYPE_TO_READINGS_API_PARAMS[deviceCategory];

  // Check the cache before making API calls
  const cachedData = await getReadingsFromCache(
    userProfileId,
    deviceId,
    readingType
  );
  if (cachedData) {
    return cachedData;
  }

  try {
    const dataApiResponse = fetchDataFromDataApi(
      deviceParam,
      userProfileId,
      readingType
    );
    const latestReadingFromConsumerApi = fetchLatestReadingFromConsumerApi(
      userProfileId,
      deviceId
    );

    const results = await Promise.allSettled([
      dataApiResponse,
      latestReadingFromConsumerApi,
    ]);

    const dataApiResult =
      results[0].status === "fulfilled" ? results[0].value : { readings: [] };
    const latestReadingConsumerApiResult =
      results[1].status === "fulfilled" ? results[1].value : null;

    logger.info(
      "latestReadingConsumerApiResult",
      latestReadingConsumerApiResult
    );
    logger.info(dataApiResult);

    const latestReadingFromDataApi = get(dataApiResult, "readings[0]", null);

    if (latestReadingConsumerApiResult) {
      if (latestReadingFromDataApi) {
        const shouldUseConsumerLatestReading =
          shouldUseConsumerApiLatestReading(
            latestReadingConsumerApiResult,
            latestReadingFromDataApi
          );

        if (shouldUseConsumerLatestReading) {
          dataApiResult.readings.unshift(latestReadingConsumerApiResult);
        }
      } else {
        dataApiResult.readings = [latestReadingConsumerApiResult];
      }
    }

    // Store the result in the cache
    setReadingsInCache(userProfileId, deviceId, readingType, dataApiResult);

    return dataApiResult;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

/**
 * Consumer api to get the latest device reading for a specific user profile and device id.
 *
 * @param {string} userProfileId
 * @param {string} deviceId - the device id
 * @returns {object} the response data
 */
readingsApi.getLatestReadingFromConsumerApi = async (
  userProfileId,
  deviceId
) => {
  // Fetch the latest reading data for a given userProfileId and deviceId from the Consumer API,
  // and handle any errors by throwing and logging them. Return the fetched data if the request is successful.
  try {
    const response = await axios.get(
      `${consumerBaseUrl}/patient_portal/user_profiles/${userProfileId}/devices/${deviceId}/latest_reading`
    );
    logger.info(response.data);
    return response.data.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

/**
 * Consumer api to get the latest share history for a specific user profile.
 *
 * @param {string} userProfileId
 * @returns {object} the response data
 */
readingsApi.getShareHistory = async (userProfileId) => {
  // Fetch the readings shares history for a given userProfileId from the Consumer API
  try {
    const response = await axios.get(
      `${consumerBaseUrl}/user_profiles/${userProfileId}/readings_shares`
    );
    logger.info("readings shares history", response);
    return response.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

/*
 * create a post request to create a share history
 * @param {string} userProfileId
 * @param {string} shareHistory
 * @returns {object} the response data
 */
readingsApi.postShareHistory = async (userProfileId, body) => {
  // Create a new readings share record for a given userProfileId using the Consumer API and the provided request body
  try {
    const response = await axios.post(
      `${consumerBaseUrl}/user_profiles/${userProfileId}/readings_shares`,
      body
    );
    logger.info("readings shares created", body);
    return response.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

export default readingsApi;
